<template>
  <div class="container-fluid p-3">
    <div class="flex items-center justify-between"
      ><div
        ><h3 class="h3 my-0 mx-0 text-foreground"
          >Reviews - {{ user.business.business_name }}</h3
        ><p class="text-muted-foreground text-sm"
          >Manage and respond to customer reviews
        </p></div
      >
      <div class="flex items-center space-x-4">
        <router-link
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
          ><i class="uil uil-star"></i
          ><span class="text-sm font-medium"> Request Review</span>
        </router-link></div
      >
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
    <div class="bg-card/50 rounded-2xl p-4 mb-4 border shadow-card reviews-stats-card">
      <h3 class="reviews-stats-title">
        Reviews & ratings over time
      </h3>

      <!-- Overview Stats -->
      <div class="row text-center mb-4">
        <div class="col">
          <div class="reviews-stats-value">{{
            formatNumber(totalReviews)
          }}</div>
          <div class="reviews-stats-label">Total Reviews</div>
        </div>
        <div class="col">
          <div class="d-flex align-items-center justify-content-center">
            <span class="reviews-stats-value mr-1">{{
              currentRating
            }}</span>
            <i class="uil uil-star text-warning reviews-stats-star"></i>
          </div>
          <div class="reviews-stats-label">Current Rating</div>
        </div>
        <div class="col">
          <div class="reviews-stats-value">{{
            newReviews
          }}</div>
          <div class="reviews-stats-label">New Reviews This Month</div>
        </div>
      </div>

      <!-- Chart -->
      <div v-if="loadingChart" class="text-center py-8">
        <b-spinner></b-spinner>
        <p class="text-muted-foreground mt-2">Loading chart data...</p>
      </div>
      <div v-else>
        <line-chart
          :chart-data="chartData"
          :chart-options="chartOptions"
        ></line-chart>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs-container">
      <div class="tabs-list">
        <button
          type="button"
          role="tab"
          class="sub-tab-button"
          :class="{ active: activeTab === 'reviews' }"
          @click="activeTab = 'reviews'"
        >
          Reviews
        </button>
        <button
          type="button"
          role="tab"
          class="sub-tab-button"
          :class="{ active: activeTab === 'feedback' }"
          @click="switchToFeedbackTab"
        >
          Internal Feedback
        </button>
      </div>
    </div>

    <!-- Tab Panels -->
    <div class="tab-panels">
      <!-- Reviews Tab -->
      <div
        v-show="activeTab === 'reviews'"
        role="tabpanel"
        class="tab-panel"
      >
        <ReviewList />
      </div>

      <!-- Internal Feedback Tab -->
      <div
        v-show="activeTab === 'feedback'"
        role="tabpanel"
        class="tab-panel"
      >
        <div v-if="loadingFeedback" class="text-center py-8">
          <b-spinner></b-spinner>
        </div>
        <div v-else-if="datatable.items.length">
            <div class="table-responsive mb-0">
              <table class="feedback-table">
                <thead>
                  <tr>
                    <th class="text-left">Name</th>
                    <th class="text-left">Email</th>
                    <th class="text-left">Feedback</th>
                    <th class="text-left">Rating</th>
                    <th class="text-left">Date</th>
                    <th class="text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in datatable.items"
                    :key="item.id"
                    class="feedback-row"
                    :class="{ 'feedback-highlight': Number(item.id) === Number(highlightedFeedbackId) }"
                    @click="openFeedback(item)"
                  >
                    <td>{{ item.name }}</td>
                    <td>{{ item.email }}</td>
                    <td class="feedback-text">{{ item.feedback }}</td>
                    <td class="feedback-rating">
                      <span v-if="item.rating">{{ item.rating }}/5 <span class="feedback-star">★</span></span>
                      <span v-else class="text-muted-foreground">—</span>
                    </td>
                    <td>{{ formatDate(item.front_date || item.created_at) }}</td>
                    <td>
                      <span
                        class="status-badge"
                        :class="getStatusClass(item)"
                      >
                        {{ getStatusText(item) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="flex items-center justify-between mt-4 text-sm text-muted-foreground">
              <span>Showing {{ showingFrom }} to {{ showingTo }} of {{ datatable.totalItems }} results</span>
              <div class="flex items-center space-x-1">
                <button
                  class="pagination-btn"
                  :disabled="datatable.queries.page === 1"
                  @click="goToFirstPage"
                >
                  «
                </button>
                <button
                  class="pagination-btn"
                  :disabled="datatable.queries.page === 1"
                  @click="goToPreviousPage"
                >
                  ‹
                </button>
                <div class="pagination-page-number">
                  {{ datatable.queries.page }}
                </div>
                <button
                  class="pagination-btn"
                  :disabled="datatable.queries.page >= totalPages"
                  @click="goToNextPage"
                >
                  ›
                </button>
                <button
                  class="pagination-btn"
                  :disabled="datatable.queries.page >= totalPages"
                  @click="goToLastPage"
                >
                  »
                </button>
              </div>
            </div>
        </div>
        <div v-else class="mt-5">
          <h3 class="text-center">You do not currently have any feedback</h3>
        </div>
      </div>
    </div>
    <FeedbackDetailModal
      v-model="showFeedbackModal"
      :feedback="selectedFeedback"
      :loading="loadingSelectedFeedback"
    />
  </div>
</template>

<script>
import '../../../design/custom/components/_dashboard.scss'
import ReviewList from './../Components/ReviewList.vue'
import FeedbackDetailModal from './../Components/FeedbackDetailModal.vue'
import { Line, mixins } from 'vue-chartjs'
import axios from 'axios'
const { reactiveProp } = mixins

export default {
  name: 'Reviews',
  components: {
    lineChart: {
      extends: Line,
      mixins: [reactiveProp],
      props: ['chartData', 'chartOptions'],
      mounted() {
        this.renderChart(this.chartData, this.chartOptions)
      },
    },
    ReviewList,
    FeedbackDetailModal,
  },
  data() {
    return {
      activeTab: 'reviews',
      loadingFeedback: false,
      highlightedFeedbackId: null,
      selectedFeedback: null,
      showFeedbackModal: false,
      loadingSelectedFeedback: false,
      datatable: {
        items: [],
        totalItems: 1,
        columns: [
          { key: 'name', label: 'Name', thStyle: { width: '150px' } },
          { key: 'email', label: 'Email' },
          { key: 'feedback', label: 'Feedback' },
          { key: 'front_date', label: 'Date' },
          { key: 'status', label: 'Status' },
        ],
        queries: {
          per_page: 10,
          page: 1,
        },
      },
      loadingChart: false,
      totalReviews: 0,
      currentRating: 0,
      newReviews: 0,
      months: [],
      reviewCounts: [],
      ratings: [],
      reviews: [
        {
          initials: 'AB',
          name: 'Anthony Baker',
          stars: 5,
          timeAgo: '2 days ago',
          platform: 'Google',
          text: 'This is a great service!',
          replyText: 'Thanks for your feedback!',
          repliedAt: '1 day ago',
          shared: {
            facebook: '2025-11-10',
            instagram: '2025-11-09',
            google: '2025-11-10',
          },
        },
        {
          initials: 'CD',
          name: 'Catherine Doe',
          stars: 4,
          timeAgo: '3 days ago',
          platform: 'Facebook',
          text: 'Very satisfied with the product!',
          replyText: null,
          repliedAt: null,
          shared: {},
        },
        // Add more reviews here
      ],
      googleReviewCount: '1.2K',
      googleRating: 4.6,
    }
  },

  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    businessUniqueId() {
      return this.user?.business?.unique_id
    },
    businessId() {
      return this.user?.business?.id
    },
    showingFrom() {
      if (this.datatable.totalItems === 0) return 0
      return (this.datatable.queries.page - 1) * this.datatable.queries.per_page + 1
    },
    showingTo() {
      const end = this.datatable.queries.page * this.datatable.queries.per_page
      return Math.min(end, this.datatable.totalItems)
    },
    totalPages() {
      return Math.ceil(this.datatable.totalItems / this.datatable.queries.per_page)
    },
    chartData() {
      return {
        labels: this.months,
        datasets: [
          {
            type: 'bar',
            label: 'Reviews',
            data: this.reviewCounts,
            backgroundColor: '#2463EB',
            yAxisID: 'y-reviews',
          },
          {
            type: 'line',
            label: 'Rating',
            data: this.ratings,
            borderColor: '#FFC107',
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            fill: false,
            yAxisID: 'y-rating',
            lineTension: 0.2,
            pointRadius: 5,
            pointBackgroundColor: '#FFC107',
            spanGaps: true,
          },
        ],
      }
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              id: 'y-reviews',
              position: 'left',
              ticks: { beginAtZero: true },
              gridLines: {
                drawTicks: false,
                drawBorder: false,
                display: false,
              },
              barPercentage: 0.6,
              categoryPercentage: 0.8,
            },
            {
              id: 'y-rating',
              position: 'right',
              ticks: { min: 1, max: 5, stepSize: 1 },
              gridLines: { drawOnChartArea: false, display: false },
            },
          ],
          xAxes: [
            {
              ticks: { autoSkip: false },
              gridLines: { display: false },
              categoryPercentage: 0.8,
              barPercentage: 0.6,
              offset: true,
            },
          ],
        },
        legend: { position: 'bottom' },
        tooltips: { mode: 'index', intersect: false },
      }
    },
  },
  mounted() {
    // Load chart data and stats
    if (this.businessUniqueId && this.businessId) {
      this.fetchChartData()
      this.fetchStats()
    }
    if (this.$route.query.tab === 'feedback' || this.$route.query.feedback) {
      this.activeTab = 'feedback'
    }
    // Only load feedback if we start on the feedback tab
    if (this.activeTab === 'feedback') {
      this.getFeedback()
    }
    if (this.$route.query.feedback) {
      this.openFeedbackById(this.$route.query.feedback)
    }
  },
  methods: {
    openFeedback(item) {
      this.highlightedFeedbackId = item.id
      this.selectedFeedback = item
      this.showFeedbackModal = true
    },
    openFeedbackById(feedbackId) {
      this.highlightedFeedbackId = feedbackId
      this.selectedFeedback = null
      this.loadingSelectedFeedback = true
      this.showFeedbackModal = true
      this.$store
        .dispatch('reviews/getFeedback', { businessId: this.businessId, feedbackId })
        .then((res) => {
          this.selectedFeedback = res.data
        })
        .catch(() => {
          this.selectedFeedback = null
        })
        .finally(() => {
          this.loadingSelectedFeedback = false
        })
    },
    onPageChanged(page) {
      this.datatable.queries.page = page
      this.getFeedback()
    },
    getFeedback() {
      this.loadingFeedback = true
      this.$store
        .dispatch('reviews/getAllFeedback', {
          queries: this.datatable.queries,
          page: 'index',
          businessId: this.user.business.id,
        })
        .then((res) => {
          console.log(res)
          this.datatable.items = res.data
          this.datatable.totalItems = res.total
          this.loadingFeedback = false
        })
        .catch(() => {
          this.loadingFeedback = false
        })
    },
    getStatusText(item) {
      // Check if feedback is resolved - adjust based on actual API response
      if (item.resolved || item.status === 'resolved' || item.resolved_at) {
        return 'Resolved'
      }
      return 'Unresolved'
    },
    getStatusClass(item) {
      const isResolved = item.resolved || item.status === 'resolved' || item.resolved_at
      return isResolved ? 'status-resolved' : 'status-unresolved'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
    },
    goToFirstPage() {
      if (this.datatable.queries.page > 1) {
        this.datatable.queries.page = 1
        this.onPageChanged(1)
      }
    },
    goToPreviousPage() {
      if (this.datatable.queries.page > 1) {
        this.datatable.queries.page--
        this.onPageChanged(this.datatable.queries.page)
      }
    },
    goToNextPage() {
      if (this.datatable.queries.page < this.totalPages) {
        this.datatable.queries.page++
        this.onPageChanged(this.datatable.queries.page)
      }
    },
    goToLastPage() {
      if (this.datatable.queries.page < this.totalPages) {
        this.datatable.queries.page = this.totalPages
        this.onPageChanged(this.totalPages)
      }
    },
    switchToFeedbackTab() {
      this.activeTab = 'feedback'
      // Always load feedback data when switching to this tab
      if (!this.loadingFeedback) {
        this.getFeedback()
      }
    },
    async fetchStats() {
      try {
        if (!this.businessUniqueId) {
          console.error('Business unique_id is required for stats')
          return
        }
        const response = await axios.get(`/re-stats/${this.businessUniqueId}`)
        
        if (!response.data || !response.data.success) {
          throw new Error('Invalid response from stats API')
        }
        
        const data = response.data.data || {}
        
        // Update stats
        this.totalReviews = data.total === null ? null : data.total || 0
        this.currentRating = data.average ?? 0
        this.newReviews = data.this_period_reviews || 0
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    },
    async fetchChartData() {
      this.loadingChart = true
      try {
        if (!this.businessId) {
          console.error('Business id is required for chart data')
          this.loadingChart = false
          return
        }
        
        // Fetch all reviews to calculate monthly data
        // We'll fetch multiple pages to get all historical data
        let allReviews = []
        let page = 1
        let hasMore = true
        const perPage = 100
        
        while (hasMore && page <= 20) { // Limit to 20 pages (2000 reviews max) to avoid infinite loops
          const response = await axios.get(`/businessreviews/${this.businessId}`, {
            params: {
              per_page: perPage,
              page: page,
              sort_by: 'review_timestamp',
              sort_desc: false, // Get oldest first to ensure we get all historical data
            },
          })

          const reviews = response.data?.data || []
          const meta = response.data?.meta || {}
          
          if (reviews.length > 0) {
            allReviews = allReviews.concat(reviews)
            // Check if there are more pages
            const totalReviews = meta.review_total || 0
            const totalPages = Math.ceil(totalReviews / perPage)
            hasMore = page < totalPages && reviews.length === perPage
            page++
          } else {
            hasMore = false
          }
        }
        
        // Group reviews by month
        const monthlyData = this.groupReviewsByMonth(allReviews)
        
        // Generate chart data for last 12 months
        this.generateChartData(monthlyData)
      } catch (error) {
        console.error('Error fetching chart data:', error)
        // Set default empty data
        this.months = []
        this.reviewCounts = []
        this.ratings = []
      } finally {
        this.loadingChart = false
      }
    },
    groupReviewsByMonth(reviews) {
      const monthlyMap = {}
      
      reviews.forEach(review => {
        if (!review.review_timestamp) return
        
        const date = new Date(review.review_timestamp)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        
        if (!monthlyMap[monthKey]) {
          monthlyMap[monthKey] = {
            reviews: [],
            ratings: []
          }
        }
        
        monthlyMap[monthKey].reviews.push(review)
        if (review.review_rating) {
          monthlyMap[monthKey].ratings.push(review.review_rating)
        }
      })
      
      return monthlyMap
    },
    generateChartData(monthlyData) {
      // Get last 12 months
      const months = []
      const reviewCounts = []
      const ratings = []
      
      const now = new Date()
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        
        // Format month label (e.g., "May '24")
        const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
        months.push(monthLabel)
        
        // Get data for this month
        const monthData = monthlyData[monthKey] || { reviews: [], ratings: [] }
        reviewCounts.push(monthData.reviews.length)
        
        // Average rating for the month; null when no reviews so the line
        // spans gaps instead of dropping to 0
        if (monthData.ratings.length > 0) {
          const avgRating =
            monthData.ratings.reduce((sum, rating) => sum + rating, 0) /
            monthData.ratings.length
          ratings.push(parseFloat(avgRating.toFixed(1)))
        } else {
          ratings.push(null)
        }
      }
      
      this.months = months
      this.reviewCounts = reviewCounts
      this.ratings = ratings
    },
    formatNumber(num) {
      if (num === null || num === undefined) {
        return ''
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },
  },
}
</script>

<style lang="scss" scoped>
td {
  margin: 0;
  padding: 0;
}

.tabs-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.tabs-list {
  display: inline-flex;
  height: 40px;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px;
  background: #f1f5f9;
  padding: 4px;
  color: #6b7280;
  width: fit-content;
}

.sub-tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.active {
    background: white;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.tab-panels {
  width: 100%;
}

.tab-panel {
  width: 100%;
}

.feedback-table {
  width: 100%;
  border-collapse: collapse;
  background: white;

  thead {
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    th {
      padding: 12px 16px;
      text-align: left;
      font-weight: 600;
      font-size: 14px;
      color: #1f2937;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e5e7eb;

      &:hover {
        background: #f9fafb;
      }

      td {
        padding: 12px 16px;
        font-size: 14px;
        color: #374151;
      }
    }
  }
}

.feedback-text {
  max-width: 400px;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.feedback-row {
  cursor: pointer;
}

.feedback-table tbody tr.feedback-highlight {
  background: #fef3c7;
}

.feedback-rating {
  white-space: nowrap;
}

.feedback-star {
  color: #ffb400;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-resolved {
  background-color: #10b981;
  color: white;
}

.status-unresolved {
  background-color: #f97316;
  color: white;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.pagination-page-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: rgb(36, 99, 235);
  color: white;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  margin: 0 4px;
}

.reviews-stats-card {
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.reviews-stats-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  font-family: inherit;
  line-height: 1.5;
}

.reviews-stats-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  font-family: inherit;
  line-height: 1.5;
}

.reviews-stats-label {
  font-size: 14px;
  color: #6b7280;
  font-family: inherit;
  line-height: 1.5;
  margin-top: 4px;
}

.reviews-stats-star {
  font-size: 14px;
}
</style>
